/**
 * Furlouve premium motion layer.
 *
 * Foundational smooth-scroll (Lenis) + scroll-driven animation (GSAP +
 * ScrollTrigger) setup, shared by every page. GSAP/ScrollTrigger/Lenis are
 * loaded as classic UMD scripts (see snippets/scripts.liquid) and attach to
 * window.gsap / window.ScrollTrigger / window.Lenis before this module runs.
 *
 * Respects prefers-reduced-motion: when set, Lenis is never initialized
 * (native scroll stays untouched) and `motionEnabled` is exported so any
 * section-level animation (e.g. the hero entrance) can skip straight to its
 * final state instead of animating.
 */
import { prefersReducedMotion } from '@theme/utilities';

export const motionEnabled = !prefersReducedMotion();

/** @type {any} Lenis instance — untyped, no local package for the vendored UMD build. */
export let lenis = null;

if (motionEnabled && typeof Lenis !== 'undefined') {
  lenis = new Lenis({
    duration: 1.2,
    easing: (/** @type {number} */ t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });

  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((/** @type {number} */ time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  } else {
    const raf = (/** @type {number} */ time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }

  // Any overlay (drawer, search modal, cart, future additions) dispatches
  // this on document when it opens/closes, instead of each one having to
  // know about Lenis directly.
  document.addEventListener('flv:scroll-lock-change', (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.locked) {
      lenis.stop();
    } else {
      lenis.start();
    }
  });
}

/**
 * Reveal an element (or each match of a selector) as it scrolls into view:
 * fade in + slight upward slide + slight scale-up, with optional stagger
 * across children. GPU-accelerated (transform/opacity only).
 *
 * No-ops immediately to the final visible state when reduced-motion is on
 * or GSAP isn't available, so callers never need their own fallback branch.
 *
 * @param {string | Element | Element[]} target
 * @param {object} [options]
 * @param {number} [options.y=32] - Starting vertical offset in px.
 * @param {number} [options.x=0] - Starting horizontal offset in px (negative = from the left, positive = from the right) — for alternating left/right section reveals.
 * @param {number} [options.scale=0.96] - Starting scale.
 * @param {number} [options.duration=1] - Animation duration in seconds.
 * @param {number} [options.stagger=0.12] - Delay between each matched element.
 * @param {number} [options.delay=0] - Initial delay before the animation starts.
 * @param {string} [options.start='top 85%'] - ScrollTrigger start position.
 */
export function revealOnScroll(target, options = {}) {
  const { y = 32, x = 0, scale = 0.96, duration = 1, stagger = 0.12, delay = 0, start = 'top 85%' } = options;
  const elements = typeof target === 'string' ? document.querySelectorAll(target) : target;

  // Callers commonly pass selectors for content that only exists on some
  // pages (e.g. a homepage-only block) — silently no-op rather than handing
  // ScrollTrigger an empty/undefined trigger element.
  const elementCount = elements instanceof NodeList || Array.isArray(elements) ? elements.length : elements ? 1 : 0;
  if (elementCount === 0) return;

  if (!motionEnabled || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    gsap?.set(elements, { opacity: 1, y: 0, x: 0, scale: 1 });
    return;
  }

  gsap.set(elements, { opacity: 0, y, x, scale });
  gsap.to(elements, {
    opacity: 1,
    y: 0,
    x: 0,
    scale: 1,
    duration,
    delay,
    stagger,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: Array.isArray(elements) || elements instanceof NodeList ? elements[0] : elements,
      start,
      once: true,
    },
  });
}
